import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/helpers";
import {
  LayoutDashboard, Home, Users, ClipboardList, CalendarCheck, ShieldCheck,
  Trophy, FileSpreadsheet, FileBarChart, Crown, LogOut, Menu, X, CalendarClock, CreditCard, ReceiptText, Award, Wallet, MonitorSmartphone, HandCoins, ScrollText, UserSquare, LifeBuoy, MessagesSquare, Banknote, PauseCircle, FileSignature,
} from "lucide-react";
import { ConsentGate, SuspensionBanner } from "@/components/ConsentGate";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { GlobalSearch } from "@/components/GlobalSearch";
import { NotificationBell } from "@/components/NotificationBell";
import { ChatBubble } from "@/components/ChatBubble";
import { cn } from "@/lib/utils";
import { useAttendanceHeartbeat } from "@/hooks/useAttendanceHeartbeat";

const NAV = [
  { to: "/", label: "Home", icon: Home, roles: ["founder", "admin", "employee"], end: true },
  { to: "/dashboard", label: "Executive Dashboard", icon: LayoutDashboard, roles: ["founder", "admin", "employee"] },
  { to: "/management", label: "Management Overview", icon: Crown, roles: ["founder", "admin"] },
  { to: "/employees", label: "Employee Master", icon: Users, roles: ["founder", "admin"] },
  { to: "/id-cards", label: "ID Cards", icon: CreditCard, roles: ["founder", "admin", "employee"] },
  { to: "/certificates", label: "Welcome Certificate", icon: Award, roles: ["founder", "admin", "employee"] },
  { to: "/tasks", label: "Task Allocation", icon: ClipboardList, roles: ["founder", "admin", "employee"] },
  { to: "/invoices", label: "Invoice Generator", icon: ReceiptText, roles: ["founder", "admin", "employee"] },
  { to: "/payslips", label: "Payslips", icon: Wallet, roles: ["founder", "admin", "employee"] },
  { to: "/payouts", label: "Salary Payouts", icon: Banknote, roles: ["founder", "admin", "employee"] },
  { to: "/loans", label: "Employee Loans", icon: HandCoins, roles: ["founder", "admin", "employee"] },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck, roles: ["founder", "admin", "employee"] },
  { to: "/leaves", label: "Leave & Holidays", icon: CalendarClock, roles: ["founder", "admin", "employee"] },
  { to: "/quality", label: "Quality Check", icon: ShieldCheck, roles: ["founder", "admin", "employee"] },
  { to: "/performance", label: "Performance", icon: Trophy, roles: ["founder", "admin", "employee"] },
  { to: "/workspace", label: "Employee Workspace", icon: FileSpreadsheet, roles: ["founder", "admin", "employee"] },
  { to: "/reports", label: "Reports & Export", icon: FileBarChart, roles: ["founder", "admin", "employee"] },
  { to: "/security", label: "Login Activity", icon: MonitorSmartphone, roles: ["founder", "admin", "employee"] },
  { to: "/chat", label: "Team Chat", icon: MessagesSquare, roles: ["founder", "admin", "employee"] },
  { to: "/profile", label: "Employee 360°", icon: UserSquare, roles: ["founder", "admin", "employee"] },
  { to: "/it-support", label: "IT Support", icon: LifeBuoy, roles: ["founder", "admin", "employee"] },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, roles: ["founder"] },
  { to: "/suspension", label: "Suspension & Consent", icon: PauseCircle, roles: ["founder"] },
  { to: "/consent", label: "My Consent", icon: FileSignature, roles: ["admin", "employee"] },
];

const ROLE_LABEL = { founder: "Founder & Head", admin: "Data Entry Head", employee: "Data Entry Operator" };

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="text-right hidden md:block" data-testid="live-clock">
      <div className="font-heading text-sm font-semibold text-navy tracking-tight">
        {now.toLocaleTimeString("en-GB")}
      </div>
      <div className="text-xs text-slate-500">
        {now.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const role = user?.role || "employee";
  const items = NAV.filter((n) => n.roles.includes(role));
  useAttendanceHeartbeat();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 bg-gold text-navy-900 transition-transform duration-300 no-print",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="flex items-center gap-3 px-5 h-20 border-b border-navy/15">
          <img src={LOGO} alt="VNS" className="h-11 w-11 object-contain rounded-md bg-white p-0.5" />
          <div>
            <div className="font-heading text-sm font-bold leading-tight text-navy-900">VNS Vinayaka</div>
            <div className="text-[10px] uppercase tracking-widest text-navy/60">Group of Enterprises</div>
          </div>
        </div>
        <nav className="px-3 py-4 space-y-1 overflow-y-auto h-[calc(100vh-5rem)]">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-navy text-gold shadow-sm"
                    : "text-navy-900 hover:bg-white/50"
                )
              }
            >
              <n.icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
              <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-20 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6 no-print">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-navy" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">
              {open ? <X /> : <Menu />}
            </button>
            <div>
              <div className="font-heading text-base font-bold text-navy tracking-tight">
                Operations Management System
              </div>
              <div className="text-xs text-slate-500">Data Entry Division</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <GlobalSearch />
            <ChatBubble />
            <NotificationBell />
            <Clock />
            <div className="flex items-center gap-3 border-l border-slate-200 pl-4">
              <Avatar className="h-9 w-9 border border-gold/40">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-navy text-gold text-xs">
                  {user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block">
                <div className="text-sm font-semibold text-navy leading-tight">{user?.name}</div>
                <div className="text-[11px] text-gold-dark font-medium">{ROLE_LABEL[role]}</div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={logout}
                data-testid="logout-btn"
                className="text-slate-500 hover:text-red-600"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <SuspensionBanner />
        <ConsentGate />
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden no-print" onClick={() => setOpen(false)} />}
    </div>
  );
}
